import React from "react";
import { interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { useLanguage } from "../../locales/LanguageContext";

const ThirdStage = ({
  stageTiming,
  targetLevel,
  bullishNumber,
  turnNeutral,
  firstUnit: propFirstUnit,
  secondUnit: propSecondUnit,
  viewLabel: propViewLabel,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const {
    t,
    lang,
    firstUnit: ctxFirstUnit,
    secondUnit: ctxSecondUnit,
    viewLabel: ctxViewLabel,
  } = useLanguage();

  const firstUnit = propFirstUnit || ctxFirstUnit;
  const secondUnit = propSecondUnit || ctxSecondUnit;
  const viewLabel = propViewLabel || ctxViewLabel;

  const bullishText =
    firstUnit && secondUnit
      ? lang === "en"
        ? `${viewLabel || "Bullish"} ${firstUnit} vs. ${secondUnit}`
        : lang === "tc-CN" || lang === "yue-CN"
          ? `看好 ${firstUnit}兑${secondUnit}`
          : `卖出 ${firstUnit} / 买入 ${secondUnit}`
      : t?.secondFrame?.thirdStage?.bullish || "Bullish";

  const decimals = lang === "en" ? 2 : 4;

  const STAGE_1_START = stageTiming;
  const STAGE_2_START = stageTiming + 180;
  const STAGE_3_START = stageTiming + 420;

  const bearishProgress = spring({
    fps,
    frame: frame - STAGE_1_START,
    config: { damping: 14 },
  });

  const bearishNumberScaleProgress = spring({
    fps,
    frame: frame - STAGE_1_START,
    config: { damping: 20, stiffness: 90 },
  });

  const bearishNumberScale = interpolate(
    bearishNumberScaleProgress,
    [0, 1],
    [20, 1.0],
  );

  const bearishCounter = interpolate(
    frame,
    [STAGE_1_START, STAGE_1_START + 120],
    [0, targetLevel],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  const targetLevelCounter = interpolate(
    frame,
    [STAGE_2_START + 20, STAGE_2_START + 140],
    [0, bullishNumber],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  const turnNeutralCounter = interpolate(
    frame,
    [STAGE_3_START + 20, STAGE_3_START + 140],
    [0, turnNeutral],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  const bearishTranslateY_Stage2 = spring({
    fps,
    frame: frame - STAGE_2_START,
    config: { damping: 14 },
  });
  const bearishTranslateY_Stage3 = spring({
    fps,
    frame: frame - STAGE_3_START,
    config: { damping: 14 },
  });

  const bearishY =
    interpolate(bearishTranslateY_Stage2, [0, 1], [0, 200]) -
    interpolate(bearishTranslateY_Stage3, [0, 1], [0, 440]);

  const mainOpacity = interpolate(
    frame,
    [stageTiming - 20, stageTiming],
    [0, 1],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  const targetLevelProgress = spring({
    fps,
    frame: frame - STAGE_2_START - 20,
    config: { damping: 14 },
  });

  const greenArrowProgress = spring({
    fps,
    frame: frame - STAGE_2_START - 20,
    config: {
      damping: 8,
      stiffness: 120,
    },
  });

  const targetLevelFadeOut = interpolate(
    frame,
    [STAGE_3_START, STAGE_3_START + 10],
    [1, 0],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  const neutralProgress = spring({
    fps,
    frame: frame - STAGE_3_START - 20,
    config: { damping: 14 },
  });

  const redArrowProgress = spring({
    fps,
    frame: frame - STAGE_3_START - 20,
    config: {
      damping: 8,
      stiffness: 120,
    },
  });
  return (
    <div
      style={{
        position: "absolute",
        top: "40%",
        left: "70%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        opacity: mainOpacity,
      }}
    >
      <div
        style={{
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          lineHeight: "1",
          alignItems: "center",
          transform: `translateY(-220px) scale(${targetLevelProgress})`,
          opacity: targetLevelFadeOut,
        }}
      >
        <p
          style={{
            fontSize: "3.2rem",
            margin: 0,
            fontWeight: "bold",
            whiteSpace: "nowrap",
          }}
        >
          {t.secondFrame.thirdStage.targetLevel}
        </p>
        <p style={{ fontSize: "11.5rem", margin: 0, fontWeight: "700" }}>
          {targetLevelCounter.toFixed(decimals)}
        </p>
      </div>

      <div
        style={{
          position: "absolute",
          top: "50px",
          left: "50%",
          transform: `translateX(-50%) scale(${greenArrowProgress})`,
          opacity: targetLevelFadeOut,
        }}
      >
        <svg width="110" height="110" viewBox="0 0 100 100">
          <polygon points="50,15 95,85 5,85" fill="#32cd32" />
        </svg>
      </div>

      <div
        style={{
          position: "absolute",
          display: "flex",
          fontSize: "1.5rem",
          flexDirection: "column",
          lineHeight: "1",
          alignItems: "center",
          transform: `translateY(${bearishY}px)`,
        }}
      >
        <p
          style={{
            fontSize: "3.2rem",
            margin: 0,
            fontWeight: "bold",
            whiteSpace: "nowrap",
            transform: `scale(${bearishProgress})`,
            opacity: bearishProgress,
          }}
        >
          {bullishText}
        </p>
        <p
          style={{
            fontSize: "11.5rem",
            margin: 0,
            fontWeight: "700",
            transform: `scale(${bearishNumberScale})`,
            opacity: bearishProgress,
          }}
        >
          {bearishCounter.toFixed(decimals)}
        </p>
      </div>

      <div
        style={{
          position: "absolute",
          top: "100px",
          left: "50%",
          transform: `translateX(-50%) translateY(-20px) scale(${redArrowProgress})`,
        }}
      >
        <svg width="110" height="110" viewBox="0 0 100 100">
          <polygon points="5,15 95,15 50,85" fill="#e80000" />
        </svg>
      </div>

      <div
        style={{
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          lineHeight: "1",
          alignItems: "center",
          transform: `translateY(200px) scale(${neutralProgress})`,
        }}
      >
        <p style={{ fontSize: "11.5rem", margin: 0, fontWeight: "700" }}>
          {turnNeutralCounter.toFixed(decimals)}
        </p>
        <p
          style={{
            fontSize: "3.2rem",
            margin: 0,
            fontWeight: "bold",
            marginTop: "10px",
            whiteSpace: "nowrap",
          }}
        >
          {t.secondFrame.thirdStage.turnNeutral}
        </p>
      </div>
    </div>
  );
};

export default ThirdStage;
